import { normalizeName } from "./identity.js";

/* ═══ CLIENT AND CONTACT: ONE READER, ONE WRITER (docs/quotes/quote-saving.md) ═══
   A quote names its client in clientName, companyEntityId and the older clientId, and
   its contact in contactName, contactEntityId, contactEmail, contactPhone and a
   confirmed row under Contacts Found. Every reader (the builder, the save record,
   booking, the page) goes through readClient and readContact; every change (hand
   edits, the AI box, a version restore) goes through writeClient and writeContact, so
   the name, the link, the email and the phone stay one person. Pure. */

export const primaryContactEmail = (c) => (Array.isArray(c?.emails) && c.emails[0]) || c?.email || "";
export const primaryContactPhone = (c) => (Array.isArray(c?.phones) && c.phones[0]) || c?.phone || "";

// The confirmed row under Contacts Found (not the company row) who is the quote's
// contact: the row's name or its CRM match is the name in the Contact field.
export function confirmedPrimaryMatch(quote) {
  const contact = normalizeName(quote?.contactName || "");
  if (!contact) return null;
  return (quote?.contactMatches || []).find((m) => (
    m && m.confirmed && !m.ignored
    && (m.extracted?.role || "contact") === "contact"
    && String(m.extracted?.name || "").trim().length > 0
    && (normalizeName(m.extracted?.name) === contact || (m.matchName && normalizeName(m.matchName) === contact))
  )) || null;
}

export function quoteHasLinkedContact(quote) {
  return !!quote?.contactEntityId || !!confirmedPrimaryMatch(quote);
}

/** The client this quote names. `id` is the explicit link (the older clientId first, as
 *  booking has always read it); `record` is that client when the CRM holds it. */
export function readClient(q, clients) {
  const id = q?.clientId || q?.companyEntityId || null;
  const record = id ? (clients || []).find((c) => c.id === id) || null : null;
  return { name: q?.clientName || "", id, record, linked: !!id };
}

/* One change to the client. Exactly one key:
   - { record }   pick a CRM client (name and link follow it)
   - { name }     type a name; the link clears once the CRM is loaded (while it isn't,
                  one keystroke must not destroy a real link)
   - { unlink }   keep the name, drop the link
   A pick or a typed name is a client change, by hand or by the AI box, so it clears a code
   approved for the old client and the box's unlinked decision (ai-box-plan, decision 2). */
export function writeClient(q, change, { crmLoaded = true } = {}) {
  if ("record" in change) {
    const c = change.record;
    return { clientName: c?.name || "", companyEntityId: c?.id || null, clientId: null, approvedClientCode: null, clientUnlinked: false };
  }
  if ("name" in change) {
    return {
      clientName: change.name,
      companyEntityId: crmLoaded ? null : (q?.companyEntityId ?? null),
      clientId: crmLoaded ? null : (q?.clientId ?? null),
      approvedClientCode: null, clientUnlinked: false,
    };
  }
  if ("unlink" in change) return { companyEntityId: null, clientId: null };
  return {};
}

/** The contact this quote names: the typed name, the link, their email and phone. */
export function readContact(q, contacts) {
  const id = q?.contactEntityId || null;
  const record = id ? (contacts || []).find((c) => c.id === id) || null : null;
  return {
    name: q?.contactName || "",
    id,
    record,
    email: q?.contactEmail || "",
    phone: q?.contactPhone || "",
    linked: quoteHasLinkedContact(q),
  };
}

/* One change to the contact. Exactly one key:
   - { record }   pick a CRM contact: name, link, email and phone are that person. A
                  phone that came in with the inquiry stays when the CRM has none and
                  it is the same person.
   - { name }     type a name; typing over a linked person drops their link, email and
                  phone once the CRM is loaded
   - { unlink }   keep the name, drop the link, email and phone
   - { email } / { phone }  set a detail on an unlinked contact (website inquiries) */
export function writeContact(q, change, { crmLoaded = true } = {}) {
  if ("record" in change) {
    const c = change.record;
    // The same person: already linked as them, or an unlinked name (an inquiry's) that is
    // their name. Their inquiry phone stays when the CRM has none.
    const same = (!!c?.id && c.id === q?.contactEntityId)
      || (!q?.contactEntityId && !!normalizeName(q?.contactName || "") && normalizeName(q?.contactName || "") === normalizeName(c?.name || ""));
    return {
      contactName: c?.name || "",
      contactEntityId: c?.id || null,
      contactEmail: primaryContactEmail(c),
      contactPhone: primaryContactPhone(c) || (same ? (q?.contactPhone || "") : ""),
    };
  }
  if ("name" in change) {
    const dropsLink = !!(crmLoaded && q?.contactEntityId);
    return {
      contactName: change.name,
      contactEntityId: crmLoaded ? null : (q?.contactEntityId ?? null),
      contactEmail: dropsLink ? "" : (q?.contactEmail || ""),
      contactPhone: dropsLink ? "" : (q?.contactPhone || ""),
    };
  }
  if ("unlink" in change) return { contactEntityId: null, contactEmail: "", contactPhone: "" };
  if ("email" in change) return { contactEmail: change.email || "" };
  if ("phone" in change) return { contactPhone: change.phone || "" };
  return {};
}

/* A link that points at nobody (the record left the CRM) must not stay on the quote:
   saving or booking would link a phantom. Only once the list has loaded (a CRM is never
   empty) can that be told. Returns the patch that clears dead links, or null. */
export function deadLinkPatch(q, contacts, clients) {
  const patch = {};
  if (q?.contactEntityId && (contacts || []).length > 0 && !readContact(q, contacts).record) {
    Object.assign(patch, { contactEntityId: null, contactEmail: "" });
  }
  if (q?.companyEntityId && (clients || []).length > 0 && !(clients || []).some((c) => c.id === q.companyEntityId)) {
    patch.companyEntityId = null;
  }
  return Object.keys(patch).length ? patch : null;
}
